// ============================================================================
// Hook per gestione dati ore — Supabase
// ============================================================================

import { useState, useEffect, useCallback } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { generateDemoData } from "../lib/helpers";
import { CANTIERI as CANTIERI_DEFAULT } from "../lib/constants";

// In produzione i dettagli degli errori non vengono esposti nei DevTools
const devErr = (...args) => { if (import.meta.env.DEV) console.error(...args); };

export function useOreData() {
  const [oreData, setOreData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  useEffect(() => {
    if (isSupabaseConfigured) {
      fetchFromSupabase();

      // Aggiornamenti in tempo reale — nessun refresh manuale necessario
      const channel = supabase
        .channel("ore_lavorate_realtime")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "ore_lavorate" }, (payload) => {
          setOreData((prev) => {
            const filtered = prev.filter(
              (o) => !(o.dipendenteId === payload.new.dipendenteId && o.data === payload.new.data)
            );
            return [payload.new, ...filtered];
          });
        })
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "ore_lavorate" }, (payload) => {
          setOreData((prev) => prev.map((o) => o.id === payload.new.id ? payload.new : o));
        })
        .on("postgres_changes", { event: "DELETE", schema: "public", table: "ore_lavorate" }, (payload) => {
          setOreData((prev) => prev.filter((o) => o.id !== payload.old.id));
        })
        .subscribe((status, err) => {
          console.log("[Realtime] ore_lavorate:", status, err || "");
          setRealtimeStatus(status);
        });

      // Flush coda offline quando torna la rete
      const flushQueue = async () => {
        try {
          const queue = JSON.parse(localStorage.getItem("oreOfflineQueue") || "[]");
          if (queue.length === 0) return;
          const remaining = [];
          for (const entry of queue) {
            try {
              const { error } = await supabase.from("ore_lavorate").upsert([entry], { onConflict: "id" });
              if (error) remaining.push(entry);
            } catch { remaining.push(entry); }
          }
          localStorage.setItem("oreOfflineQueue", JSON.stringify(remaining));
          if (remaining.length < queue.length) fetchFromSupabase();
        } catch {}
      };
      window.addEventListener("online", flushQueue);
      flushQueue(); // prova subito all'avvio

      // Polling fallback: aggiorna i dati ogni 15s anche se il realtime non funziona
      const poll = setInterval(() => fetchFromSupabase(), 15000);

      return () => {
        supabase.removeChannel(channel);
        window.removeEventListener("online", flushQueue);
        clearInterval(poll);
      };
    } else {
      setOreData(generateDemoData());
      setLoading(false);
    }
  }, []);

  const fetchFromSupabase = async () => {
    try {
      // Supabase ha un limite di 1000 righe per query — paginiamo
      let allData = [];
      let from = 0;
      const pageSize = 1000;
      while (true) {
        const { data, error } = await supabase
          .from("ore_lavorate")
          .select("*")
          .order("data", { ascending: false })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allData = allData.concat(data);
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allData.length === 0) {
        // Prima connessione: carica i dati storici di Febbraio 2026
        const demo = generateDemoData();
        const { error: insertError } = await supabase
          .from("ore_lavorate")
          .insert(demo);
        if (insertError) throw insertError;
        setOreData(demo);
      } else {
        setOreData(allData);
      }
    } catch (err) {
      devErr("Errore Supabase, uso dati demo:", err);
      setOreData(generateDemoData());
    } finally {
      setLoading(false);
    }
  };

  // Aggiungi/aggiorna registrazione (con fallback offline)
  const addOre = useCallback(async (entry) => {
    const entryId = `${entry.dipendenteId}-${entry.data}`;
    const baseEntry = {
      ...entry,
      id: entryId,
      timestamp: entry.timestamp || new Date(entry.data).getTime(),
    };

    // Aggiorna subito UI locale
    setOreData((prev) => {
      const filtered = prev.filter(
        (o) => !(o.dipendenteId === entry.dipendenteId && o.data === entry.data)
      );
      return [baseEntry, ...filtered];
    });

    if (isSupabaseConfigured) {
      try {
        const { data, error } = await supabase
          .from("ore_lavorate")
          .upsert([baseEntry], { onConflict: "id" })
          .select();
        if (error) throw error;
        const saved = data[0];
        setOreData((prev) => {
          const filtered = prev.filter(
            (o) => !(o.dipendenteId === entry.dipendenteId && o.data === entry.data)
          );
          return [saved, ...filtered];
        });
        return { success: true };
      } catch (err) {
        devErr("Errore salvataggio (salvato offline):", err);
        // Salva in coda offline — verrà inviato quando torna la rete
        try {
          const queue = JSON.parse(localStorage.getItem("oreOfflineQueue") || "[]");
          const filtered = queue.filter((q) => q.id !== baseEntry.id);
          localStorage.setItem("oreOfflineQueue", JSON.stringify([...filtered, baseEntry]));
        } catch {}
        return { success: true, offline: true };
      }
    }
    return { success: true };
  }, [oreData]);

  // Upsert singola cella dalla griglia Excel admin
  const upsertOreEntry = useCallback(async (entry) => {
    // Aggiorna subito lo stato locale (UI reattiva) — prepend per mantenere ordine cronologico
    setOreData((prev) => {
      const filtered = prev.filter((o) => o.id !== entry.id);
      return [entry, ...filtered];
    });
    if (isSupabaseConfigured) {
      const { error } = await supabase
        .from("ore_lavorate")
        .upsert([entry], { onConflict: "id" });
      if (error) devErr("Errore upsert cella:", error);
    }
  }, []);

  // Cancella singola cella dalla griglia Excel admin
  const deleteOreEntry = useCallback(async (id) => {
    setOreData((prev) => prev.filter((o) => o.id !== id));
    if (isSupabaseConfigured) {
      const { error } = await supabase.from("ore_lavorate").delete().eq("id", id);
      if (error) devErr("Errore delete cella:", error);
    }
  }, []);

  return { oreData, loading, addOre, setOreData, upsertOreEntry, deleteOreEntry, refreshOreData: fetchFromSupabase, realtimeStatus };
}

// ============================================================================
// Hook per gestione cantieri — Supabase
// ============================================================================
// Entrambe le liste (attivi + archiviati) vivono nel DB: la colonna
// `archiviato` discrimina. L'archiviazione e' un UPDATE, non un DELETE,
// quindi l'evento realtime si propaga uniformemente a tutti i device.
export function useCantieri() {
  const [cantieri, setCantieri] = useState(CANTIERI_DEFAULT);
  const [archivedCantieri, setArchivedCantieri] = useState([]);

  useEffect(() => {
    if (!isSupabaseConfigured) return;

    // Pulizia una-tantum del vecchio archivio per-device (localStorage)
    try { localStorage.removeItem("archivedCantieri"); } catch {}

    const applyRows = (rows) => {
      const attivi = [];
      const archiviati = [];
      rows.forEach((r) => {
        if (r.archiviato) archiviati.push(r.nome);
        else attivi.push(r.nome);
      });
      setCantieri(attivi);
      setArchivedCantieri(archiviati);
    };

    supabase
      .from("cantieri")
      .select("nome, archiviato")
      .order("id", { ascending: true })
      .then(({ data, error }) => {
        if (error) { devErr("Errore caricamento cantieri:", error); return; }
        if (data && data.length > 0) {
          applyRows(data);
        } else {
          // Prima connessione: carica i cantieri default
          const rows = CANTIERI_DEFAULT.map((nome) => ({ nome, archiviato: false }));
          supabase.from("cantieri").insert(rows).then(({ error: e }) => {
            if (e) devErr("Errore seed cantieri:", e);
          });
        }
      });

    // Realtime — aggiornamento live cantieri su tutti i dispositivi
    const channel = supabase
      .channel("cantieri_realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "cantieri" }, (payload) => {
        const { nome, archiviato } = payload.new;
        if (archiviato) {
          setArchivedCantieri((prev) => prev.includes(nome) ? prev : [...prev, nome]);
          setCantieri((prev) => prev.filter((c) => c !== nome));
        } else {
          setCantieri((prev) => prev.includes(nome) ? prev : [...prev, nome]);
          setArchivedCantieri((prev) => prev.filter((c) => c !== nome));
        }
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "cantieri" }, (payload) => {
        const { nome, archiviato } = payload.new;
        const nomeVecchio = payload.old?.nome;
        if (nomeVecchio && nomeVecchio !== nome) {
          // Rename: rimuovi il vecchio nome da entrambe le liste
          setCantieri((prev) => prev.filter((c) => c !== nomeVecchio));
          setArchivedCantieri((prev) => prev.filter((c) => c !== nomeVecchio));
        }
        if (archiviato) {
          setCantieri((prev) => prev.filter((c) => c !== nome));
          setArchivedCantieri((prev) => prev.includes(nome) ? prev : [...prev, nome]);
        } else {
          setArchivedCantieri((prev) => prev.filter((c) => c !== nome));
          setCantieri((prev) => prev.includes(nome) ? prev : [...prev, nome]);
        }
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "cantieri" }, (payload) => {
        const nome = payload.old?.nome;
        if (!nome) return;
        setCantieri((prev) => prev.filter((c) => c !== nome));
        setArchivedCantieri((prev) => prev.filter((c) => c !== nome));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const addCantiere = useCallback(async (nome) => {
    setCantieri((prev) => prev.includes(nome) ? prev : [...prev, nome]);
    setArchivedCantieri((prev) => prev.filter((c) => c !== nome));
    if (isSupabaseConfigured) {
      const { error } = await supabase
        .from("cantieri")
        .upsert([{ nome, archiviato: false }], { onConflict: "nome" });
      if (error) devErr("Errore inserimento cantiere:", error);
    }
  }, []);

  const deleteCantiere = useCallback(async (nome) => {
    setCantieri((prev) => prev.filter((c) => c !== nome));
    setArchivedCantieri((prev) => prev.includes(nome) ? prev : [...prev, nome]);
    if (isSupabaseConfigured) {
      const { error } = await supabase
        .from("cantieri")
        .update({ archiviato: true })
        .eq("nome", nome);
      if (error) devErr("Errore archiviazione cantiere:", error);
    }
  }, []);

  const restoreCantiere = useCallback(async (nome) => {
    setArchivedCantieri((prev) => prev.filter((c) => c !== nome));
    setCantieri((prev) => prev.includes(nome) ? prev : [...prev, nome]);
    if (isSupabaseConfigured) {
      const { error } = await supabase
        .from("cantieri")
        .update({ archiviato: false })
        .eq("nome", nome);
      if (error) devErr("Errore ripristino cantiere:", error);
    }
  }, []);

  return { cantieri, setCantieri, addCantiere, deleteCantiere, restoreCantiere, archivedCantieri };
}

// ============================================================================
// Hook per scadenze contratti dipendenti
// ============================================================================
export function useScadenze() {
  const [dipDB, setDipDB] = useState({}); // { [id]: { scadenza, note } }

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    supabase
      .from("dipendenti_scadenze")
      .select("*")
      .then(({ data }) => {
        if (data && data.length > 0) {
          const map = {};
          data.forEach((r) => { map[r.id] = { scadenza: r.scadenza, note: r.note || "" }; });
          setDipDB(map);
        }
      });
  }, []);

  const saveScadenza = useCallback(async (dipId, scadenza) => {
    setDipDB((prev) => ({ ...prev, [dipId]: { ...prev[dipId], scadenza } }));
    if (!isSupabaseConfigured) return;
    await supabase.from("dipendenti_scadenze")
      .upsert([{ id: dipId, scadenza, note: dipDB[dipId]?.note || "" }], { onConflict: "id" });
  }, [dipDB]);

  const saveNote = useCallback(async (dipId, note) => {
    setDipDB((prev) => ({ ...prev, [dipId]: { ...prev[dipId], note } }));
    if (!isSupabaseConfigured) return;
    await supabase.from("dipendenti_scadenze")
      .upsert([{ id: dipId, note, scadenza: dipDB[dipId]?.scadenza || null }], { onConflict: "id" });
  }, [dipDB]);

  const mergeDipendenti = useCallback((dipendenti) => {
    return dipendenti.map((d) => ({
      ...d,
      scadenza: dipDB[d.id]?.scadenza !== undefined ? dipDB[d.id].scadenza : d.scadenza,
      note: dipDB[d.id]?.note != null ? dipDB[d.id].note : (d.note || ""),
    }));
  }, [dipDB]);

  return { saveScadenza, saveNote, mergeDipendenti };
}

// ============================================================================
// Hook per impostazioni app sincronizzate via Supabase (oreOverride, cantieriMeta)
// ============================================================================
export function useAppSettings(key, defaultValue) {
  const resolvedDefault = typeof defaultValue === "function" ? defaultValue() : defaultValue;
  const [value, setValue] = useState(() => {
    // Inizializza da localStorage come cache locale
    try { return JSON.parse(localStorage.getItem(key) || "null") ?? resolvedDefault; }
    catch { return resolvedDefault; }
  });

  useEffect(() => {
    if (!isSupabaseConfigured) return;

    // Carica valore attuale da Supabase
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", key)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          setValue(data.value);
          localStorage.setItem(key, JSON.stringify(data.value));
        }
      });

    // Sottoscrivi aggiornamenti real-time
    const channel = supabase
      .channel(`app_settings_${key}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "app_settings",
        filter: `key=eq.${key}`,
      }, (payload) => {
        if (payload.new?.value) {
          setValue(payload.new.value);
          localStorage.setItem(key, JSON.stringify(payload.new.value));
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [key]);

  const save = useCallback(async (newValue) => {
    setValue(newValue);
    localStorage.setItem(key, JSON.stringify(newValue));
    if (!isSupabaseConfigured) return;
    await supabase
      .from("app_settings")
      .upsert([{ key, value: newValue }], { onConflict: "key" });
  }, [key]);

  return [value, save];
}

// ============================================================================
// Hook per promemoria dipendenti
// ============================================================================
export function usePromemoria() {
  const [promemoria, setPromemoria] = useState([]);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    supabase
      .from("promemoria")
      .select("*")
      .order("data_scadenza", { ascending: true })
      .then(({ data, error }) => {
        if (error) { devErr("Errore caricamento promemoria:", error); return; }
        if (data) setPromemoria(data);
      });

    const channel = supabase
      .channel("promemoria_realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "promemoria" }, (payload) => {
        setPromemoria((prev) => [...prev, payload.new]);
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "promemoria" }, (payload) => {
        setPromemoria((prev) => prev.filter((p) => p.id !== payload.old.id));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const addPromemoria = useCallback(async ({ dipendente_id, testo, data_scadenza, giorni_preavviso = 2 }) => {
    if (isSupabaseConfigured) {
      const { data, error } = await supabase.from("promemoria").insert([{ dipendente_id, testo, data_scadenza, giorni_preavviso }]).select();
      if (error) { devErr("Errore aggiunta promemoria:", error); return; }
      setPromemoria((prev) => [...prev, data[0]]);
    } else {
      setPromemoria((prev) => [...prev, { id: Date.now(), dipendente_id, testo, data_scadenza, giorni_preavviso }]);
    }
  }, []);

  const deletePromemoria = useCallback(async (id) => {
    setPromemoria((prev) => prev.filter((p) => p.id !== id));
    if (isSupabaseConfigured) {
      await supabase.from("promemoria").delete().eq("id", id);
    }
  }, []);

  return { promemoria, addPromemoria, deletePromemoria };
}
